import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject, catchError, debounceTime, filter, firstValueFrom, of, switchMap, takeUntil } from 'rxjs';
import { getClientIdFromAccessToken } from '../common/jwt-utils';
import { IAuthenticate } from '../models/authenticate';
import {
    DEFAULT_THEME_SETTINGS,
    IUserUiPreferenceEnvelope,
    THEME_SCHEMA_VERSION,
    createDefaultEnvelope,
    IThemeSettings
} from '../models/theme-preference';
import { HttpHandlerService } from './http-handler.service';
import { LayoutService, layoutConfig } from '../layout/service/layout.service';
import { ThemeApplyService } from '../layout/service/theme-apply.service';
import { buildThemeStorageKey, normalizeThemeSettings } from '../layout/service/theme.constants';

@Injectable({
    providedIn: 'root'
})
export class ThemePreferenceService {
    private readonly layoutService = inject(LayoutService);
    private readonly themeApplyService = inject(ThemeApplyService);
    private readonly http = inject(HttpHandlerService);

    readonly restoreComplete$ = new BehaviorSubject<boolean>(false);

    private isRestoring = false;
    private currentUserId: number | null = null;
    private currentClientId: number | null = null;
    private currentStorageKey: string | null = null;
    private readonly saveTrigger$ = new Subject<IUserUiPreferenceEnvelope>();
    private readonly destroy$ = new Subject<void>();
    private readonly apiAvailable$ = new BehaviorSubject<boolean>(true);

    constructor() {
        this.layoutService.configUpdate$.subscribe((config) => {
            if (this.isRestoring || !this.currentStorageKey) {
                return;
            }

            const theme = this.fromLayoutConfig(config);
            this.persistLocal(this.toEnvelope(theme));
            this.saveTrigger$.next(this.toEnvelope(theme));
        });

        this.saveTrigger$
            .pipe(
                debounceTime(500),
                filter(() => this.apiAvailable$.value),
                switchMap((envelope) =>
                    this.http.post<boolean>('User/SaveUiTheme', envelope).pipe(
                        catchError(() => {
                            this.apiAvailable$.next(false);
                            return of(false);
                        })
                    )
                ),
                takeUntil(this.destroy$)
            )
            .subscribe();

        if (typeof window !== 'undefined') {
            window.addEventListener('storage', (event) => this.onStorageEvent(event));
        }
    }

    initializeFromAuthUser(user: IAuthenticate | null): void {
        if (!user?.id) {
            this.clearContext();
            return;
        }

        const accessToken = user.jwtToken?.accessToken ?? null;
        const clientId = getClientIdFromAccessToken(accessToken);
        if (!clientId) {
            return;
        }

        void this.initialize(user.id, clientId);
    }

    async initialize(userId: number, clientId: number): Promise<void> {
        this.currentUserId = userId;
        this.currentClientId = clientId;
        this.currentStorageKey = buildThemeStorageKey(clientId, userId);
        this.restoreComplete$.next(false);
        this.isRestoring = true;
        this.layoutService.suppressDarkModeTransition = true;

        const localEnvelope = this.loadLocal();
        if (localEnvelope) {
            this.applyEnvelope(localEnvelope, false);
        }

        let serverEnvelope: IUserUiPreferenceEnvelope | null = null;
        if (this.apiAvailable$.value) {
            try {
                serverEnvelope = await firstValueFrom(
                    this.http.get<IUserUiPreferenceEnvelope | null>('User/GetUiTheme').pipe(catchError(() => of(null)))
                );
            } catch {
                this.apiAvailable$.next(false);
            }
        }

        const resolved = this.resolveEnvelope(localEnvelope, serverEnvelope);
        this.applyEnvelope(resolved, true);

        if (serverEnvelope == null && localEnvelope != null && this.apiAvailable$.value) {
            this.saveTrigger$.next(resolved);
        }

        this.isRestoring = false;
        this.layoutService.suppressDarkModeTransition = false;
        this.restoreComplete$.next(true);
    }

    resetToDefaults(): void {
        if (!this.currentStorageKey) {
            return;
        }

        this.isRestoring = true;
        this.layoutService.suppressDarkModeTransition = true;

        const envelope = createDefaultEnvelope();
        this.applyEnvelope(envelope, true);
        localStorage.removeItem(this.currentStorageKey);

        if (this.apiAvailable$.value) {
            this.http.delete<boolean>('User/DeleteUiTheme').pipe(catchError(() => of(false))).subscribe();
        }

        this.isRestoring = false;
        this.layoutService.suppressDarkModeTransition = false;
    }

    private resolveEnvelope(
        local: IUserUiPreferenceEnvelope | null,
        server: IUserUiPreferenceEnvelope | null
    ): IUserUiPreferenceEnvelope {
        if (local && server) {
            const localTime = Date.parse(local.updatedAtUtc);
            const serverTime = Date.parse(server.updatedAtUtc);
            if (Number.isFinite(localTime) && Number.isFinite(serverTime) && localTime > serverTime) {
                return this.normalizeEnvelope(local);
            }
            return this.normalizeEnvelope(server);
        }

        if (server) {
            return this.normalizeEnvelope(server);
        }

        if (local) {
            return this.normalizeEnvelope(local);
        }

        return createDefaultEnvelope();
    }

    private applyEnvelope(envelope: IUserUiPreferenceEnvelope, persistLocal: boolean): void {
        const theme = normalizeThemeSettings(envelope.theme);
        this.layoutService.layoutConfig.set({ ...theme });
        this.themeApplyService.applyFromConfig(theme);
        this.layoutService.toggleDarkMode(theme);

        if (persistLocal && this.currentStorageKey) {
            this.persistLocal(this.normalizeEnvelope({ ...envelope, theme }));
        }
    }

    private toEnvelope(theme: IThemeSettings): IUserUiPreferenceEnvelope {
        return {
            schemaVersion: THEME_SCHEMA_VERSION,
            updatedAtUtc: new Date().toISOString(),
            theme: { ...theme }
        };
    }

    private normalizeEnvelope(envelope: IUserUiPreferenceEnvelope): IUserUiPreferenceEnvelope {
        const updatedAtUtc =
            typeof envelope.updatedAtUtc === 'string'
                ? envelope.updatedAtUtc
                : envelope.updatedAtUtc != null
                  ? new Date(envelope.updatedAtUtc as unknown as string).toISOString()
                  : new Date().toISOString();

        return {
            schemaVersion: envelope.schemaVersion ?? THEME_SCHEMA_VERSION,
            updatedAtUtc,
            theme: normalizeThemeSettings(envelope.theme)
        };
    }

    private loadLocal(): IUserUiPreferenceEnvelope | null {
        if (!this.currentStorageKey) {
            return null;
        }

        try {
            const raw = localStorage.getItem(this.currentStorageKey);
            if (!raw) {
                return null;
            }

            const parsed = JSON.parse(raw) as IUserUiPreferenceEnvelope;
            if (!parsed?.theme) {
                return null;
            }

            return this.normalizeEnvelope(parsed);
        } catch {
            return null;
        }
    }

    private persistLocal(envelope: IUserUiPreferenceEnvelope): void {
        if (!this.currentStorageKey) {
            return;
        }

        localStorage.setItem(this.currentStorageKey, JSON.stringify(envelope));
    }

    private onStorageEvent(event: StorageEvent): void {
        if (!this.currentStorageKey || event.key !== this.currentStorageKey || !event.newValue) {
            return;
        }

        try {
            const envelope = this.normalizeEnvelope(JSON.parse(event.newValue) as IUserUiPreferenceEnvelope);
            this.isRestoring = true;
            this.layoutService.suppressDarkModeTransition = true;
            this.applyEnvelope(envelope, false);
            this.isRestoring = false;
            this.layoutService.suppressDarkModeTransition = false;
        } catch {
            // ignore corrupt cross-tab payloads
        }
    }

    private clearContext(): void {
        this.currentUserId = null;
        this.currentClientId = null;
        this.currentStorageKey = null;
        this.restoreComplete$.next(false);
    }

    private fromLayoutConfig(config: layoutConfig): IThemeSettings {
        return normalizeThemeSettings({
            preset: config.preset,
            primary: config.primary,
            surface: config.surface ?? undefined,
            darkTheme: config.darkTheme,
            menuMode: config.menuMode
        });
    }
}

export { DEFAULT_THEME_SETTINGS, buildThemeStorageKey };
